import React, { useState, useEffect } from "react";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Coins, X, AlertCircle } from "lucide-react";

/**
 * Modal — Antecipar acerto do sócio (iter56).
 *
 * O valor é calculado AUTOMATICAMENTE pelo backend a partir de:
 *   juros_do_ciclo × SOCIO_PROFIT_SHARE (padrão 50%)
 *
 * O usuário NÃO informa mais o valor. Apenas confirma + motivo.
 * Endpoint preview: GET /api/contracts/{id}/advance-socio/preview
 * Endpoint submit:  POST /api/contracts/{id}/advance-socio
 */
export default function AdvanceSocioModal({ contract, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/contracts/${contract.id}/advance-socio/preview`)
      .then(r => setPreview(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Falha ao calcular valor da antecipação"));
  }, [contract.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { toast.error("Motivo obrigatório (mínimo 5 caracteres)"); return; }
    if (!confirmed) { toast.error("Confirme a operação"); return; }
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/advance-socio`, {
        reason: reason.trim(), notes: notes.trim(),
      });
      toast.success(`Antecipação de ${formatBRL(preview.valor_a_antecipar)} registrada`);
      onDone && onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao antecipar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="advance-socio-modal">
      <form onSubmit={submit} className="w-full max-w-lg rounded-lg border border-[#262626] bg-[#0d0d0d] shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <h3 className="font-display text-base flex items-center gap-2 text-amber-300">
            <Coins size={16}/> Antecipar acerto do sócio
          </h3>
          <button type="button" onClick={onClose} className="text-[#737373] hover:text-white" data-testid="advance-socio-close">
            <X size={18}/>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[12px] text-amber-200/90">
            <strong>Atenção:</strong> Esta ação <strong>não representa pagamento do cliente</strong>.
            O contrato continua vencido/inadimplente. Apenas a parte do sócio nos <strong>juros
            do ciclo atual</strong> será adiantada agora. Quando o cliente pagar (renovação ou
            quitação), o valor será compensado automaticamente sem creditar o sócio duas vezes.
          </div>

          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-[12px] text-red-200 flex items-start gap-2" data-testid="advance-error">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
              <span>{error}</span>
            </div>
          )}

          {!preview && !error && (
            <div className="text-[#737373] text-sm flex items-center gap-2" data-testid="advance-preview-loading">
              <Loader2 size={14} className="animate-spin"/> Calculando valor da antecipação…
            </div>
          )}

          {preview && (
            <>
              <div className="rounded-md border border-[#262626] bg-[#0a0a0a] p-4 space-y-2" data-testid="advance-preview">
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[#A3A3A3]">Ciclo atual</span>
                  <span className="font-mono-tab text-white" data-testid="advance-preview-cycle">
                    #{preview.cycle_number}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[#A3A3A3]">Juros do ciclo</span>
                  <span className="font-mono-tab text-white" data-testid="advance-preview-interest">
                    {formatBRL(preview.juros_do_ciclo)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[#A3A3A3]">Participação do sócio</span>
                  <span className="font-mono-tab text-white" data-testid="advance-preview-percent">
                    {preview.socio_share_percent}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[#A3A3A3]">Sócio</span>
                  <span className="text-white truncate max-w-[240px]" data-testid="advance-preview-socio">
                    {preview.socio_name || "—"}
                  </span>
                </div>
                <div className="border-t border-[#262626] pt-2 mt-2 flex items-center justify-between">
                  <span className="text-[13px] uppercase tracking-[0.14em] text-amber-300">Valor a antecipar</span>
                  <span className="font-mono-tab text-[18px] font-semibold text-amber-300" data-testid="advance-preview-value">
                    {formatBRL(preview.valor_a_antecipar)}
                  </span>
                </div>
              </div>

              {preview.already_advanced && (
                <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-[12px] text-red-200 flex items-start gap-2" data-testid="advance-blocked-existing">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
                  <span>Este ciclo já possui uma antecipação aberta. Compense-a via pagamento ou estorne antes de criar uma nova.</span>
                </div>
              )}
            </>
          )}

          <div>
            <label className="fc-label">Motivo <span className="text-[#737373] font-normal">(obrigatório, mínimo 5 caracteres)</span></label>
            <textarea
              rows={2} className="fc-input resize-none"
              value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Ex: cliente atrasou, sócio precisa do acerto antes do pagamento"
              required data-testid="advance-reason"
            />
          </div>

          <div>
            <label className="fc-label">Observações (opcional)</label>
            <input
              className="fc-input" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Ex: cliente prometeu pagar até dia 15"
              data-testid="advance-notes"
            />
          </div>

          <label className="flex items-start gap-2 text-[12px] text-[#A3A3A3] cursor-pointer" data-testid="advance-confirm">
            <input
              type="checkbox" checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5 accent-amber-500"
            />
            <span>
              Confirmo que a Forte Capital antecipará este valor ao sócio, ciente de
              que o cliente ainda não pagou. Registrado com usuário, data, hora e motivo.
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#1f1f1f] bg-[#0a0a0a]/50">
          <button type="button" onClick={onClose} className="fc-btn-ghost text-sm">Cancelar</button>
          <button
            type="submit"
            disabled={busy || !confirmed || !preview || preview.already_advanced || !!error}
            className="fc-btn-gold flex items-center gap-2 text-sm disabled:opacity-40"
            data-testid="advance-socio-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin"/>} Antecipar ao sócio
          </button>
        </div>
      </form>
    </div>
  );
}
